import IconCloud from '@apollo/icons/default/IconCloud.svg';
import { FormControl, FormErrorMessage, FormLabel, Input } from '@apollo/orbit';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import React, { useEffect } from 'react';
import { useWizard } from 'react-use-wizard';

import { getRouterEndpoint } from 'src/app/account/supergraphListPage/newSupergraphOnboarding/shared';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME } from 'src/app/onboarding/constants';
import { Form } from 'src/components/common/form/Form';
import { InputCharCount } from 'src/components/InputCharCount';

import { AddVariantWizardFields, VARIANT_NAME_MAX_CHAR } from './shared';

const RouterEndpointDOM = ({
  url,
  variantName,
  supergraphID,
}: {
  url: URL;
  variantName: string;
  supergraphID: string;
}) => {
  const [host0, host1] = url.host.split('--');
  return (
    <div>
      {`${url.protocol}//`}
      <span className="font-semibold">{host0 ?? variantName}</span>
      {`--${host1 ?? `${supergraphID}.${CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME}`}`}
    </div>
  );
};

export const RouterEndpoint = ({
  variantName,
  supergraphID,
}: {
  variantName: string;
  supergraphID: string;
}) => {
  const routerEndpointString = getRouterEndpoint({
    variantName,
    supergraphID,
  });
  try {
    const routerEndpointUrl = new URL(routerEndpointString);
    return (
      <RouterEndpointDOM
        url={routerEndpointUrl}
        variantName={variantName}
        supergraphID={supergraphID}
      />
    );
  } catch {
    return (
      <RouterEndpointDOM
        // if the router endpoint url parsing fails, the variant name is invalid as a URL.
        // fall back to it as an empty string
        url={new URL(getRouterEndpoint({ variantName: '', supergraphID }))}
        variantName=""
        supergraphID={supergraphID}
      />
    );
  }
};

export const NameVariantStep = ({
  values,
  errors,
  handleBlur,
  touched,
  setValues,
  variantNameAvailable,
  setCurrentStep,
  isCloudSupergraph,
}: {
  values: FormikState<AddVariantWizardFields>['values'];
  errors: FormikState<AddVariantWizardFields>['errors'];
  touched: FormikState<AddVariantWizardFields>['touched'];
  handleBlur: FormikHandlers['handleBlur'];
  setValues: FormikHelpers<AddVariantWizardFields>['setValues'];
  variantNameAvailable: boolean;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  isCloudSupergraph: boolean;
}) => {
  const { activeStep } = useWizard();
  useEffect(() => setCurrentStep(activeStep), [activeStep, setCurrentStep]);

  const graphRef = useGraphRef();
  if (!graphRef?.graphId) {
    return <div>GraphId needed to create variant</div>;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-black">Name your variant</h2>
      <div className="mt-1 text-base text-secondary">
        A <span className="italic">variant</span> represents a particular
        instance of your {isCloudSupergraph ? 'supergraph' : 'graph'}. Usually,
        a {isCloudSupergraph ? 'supergraph' : 'graph'} has a separate variant
        for each environment it runs in (main, staging, and so on.)
      </div>
      <Form>
        <FormControl
          className="mt-4"
          isInvalid={
            (!!errors.variantName && !!touched.variantName) ||
            !variantNameAvailable
          }
        >
          <FormLabel>Variant Name</FormLabel>
          <Input
            onBlur={handleBlur}
            onChange={(e) => {
              setValues(
                (fields) => ({
                  ...fields,
                  variantName: e.target.value,
                }),
                // validate on change only when there are errors
                'variantName' in errors,
              );
            }}
            value={values.variantName}
            name="variantName"
            placeholder="Name your variant"
            rightElement={
              <InputCharCount
                count={values.variantName.length}
                maxCount={VARIANT_NAME_MAX_CHAR}
              />
            }
          />
          <FormErrorMessage>
            {!!errors.variantName && !!touched.variantName
              ? errors.variantName
              : !variantNameAvailable
                ? 'This name is being used by another variant. Try a different name.'
                : ''}
          </FormErrorMessage>
        </FormControl>
        {isCloudSupergraph && (
          <>
            <h3 className="mt-6 text-base font-semibold text-black">
              Router Endpoint
            </h3>

            <div className="flex items-center">
              <IconCloud className="mr-2 mt-1 size-4 self-start text-secondary" />
              <div className="mr-2 flex break-all text-base text-secondary">
                <RouterEndpoint
                  variantName={values.variantName}
                  supergraphID={graphRef?.graphId}
                />
              </div>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};
